import axiosInstance from "./axios";
import { errorToast } from "../shared/Toast";

/**
 * /api/merchant-pages. Reads fall back to an empty value; writes return null on failure after a
 * toast — except create/update/preview, which hand back the server's `errors` list so the editor
 * can show every addressed problem, not just the first.
 */

const EMPTY_LIST = { items: [], total: 0, page: 1, limit: 25, totalPages: 0, merchant: null };

const messageOf = (error, fallback) => error?.response?.data?.message ?? error?.message ?? fallback;

const read = async (call, fallback, message) => {
  try {
    const res = await call();
    if (res?.data && !res.data.isError) return res.data.data ?? fallback;
    errorToast(res?.data?.message ?? message);
    return fallback;
  } catch (error) {
    errorToast(messageOf(error, message));
    return fallback;
  }
};

const write = async (call, message) => {
  try {
    const res = await call();
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? message);
    return null;
  } catch (error) {
    errorToast(messageOf(error, message));
    return null;
  }
};

/**
 * For saves: `{ data }` on success, `{ error, errors, errorCode }` on refusal. The first error is
 * also toasted so a refusal is never silent.
 */
const save = async (call, message) => {
  try {
    const res = await call();
    if (res?.data && !res.data.isError) return { data: res.data.data };
    errorToast(res?.data?.message ?? message);
    return { error: res?.data?.message ?? message, errors: res?.data?.data?.errors || [], errorCode: res?.data?.errorCode };
  } catch (error) {
    const body = error?.response?.data;
    const msg = messageOf(error, message);
    errorToast(msg);
    return { error: msg, errors: body?.data?.errors || [], errorCode: body?.errorCode };
  }
};

export const getPageTemplates = () =>
  read(() => axiosInstance.get("/api/merchant-pages/templates"), { templates: [] }, "Error loading templates")
    .then((d) => d.templates || []);

export const listPages = (params = {}) =>
  read(() => axiosInstance.get("/api/merchant-pages", { params }), EMPTY_LIST, "Error loading pages");

export const getPage = (id) =>
  read(() => axiosInstance.get(`/api/merchant-pages/${id}`), null, "Error loading page");

export const createPage = (body) =>
  save(() => axiosInstance.post("/api/merchant-pages", body), "Error saving page");

export const updatePage = (id, body) =>
  save(() => axiosInstance.put(`/api/merchant-pages/${id}`, body), "Error saving page");

export const deletePage = (id) =>
  write(() => axiosInstance.delete(`/api/merchant-pages/${id}`), "Error deleting page");

export const setPageStatus = (id, status) =>
  write(() => axiosInstance.post(`/api/merchant-pages/${id}/publish`, { status }), "Error changing the page status");

export const duplicatePage = (id, body = {}) =>
  write(() => axiosInstance.post(`/api/merchant-pages/${id}/duplicate`, body), "Error duplicating page");

/** `{ html, warnings }` on success; `{ error, errors }` when the draft would be refused on save. */
export const previewPage = (body) =>
  save(() => axiosInstance.post("/api/merchant-pages/preview", body), "Error building the preview");
